import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/commerce-pwa-studio/commerce-pwa-studio/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "client-side-caching"
    }}>{`Client-side caching`}</h1>
    <p>{`Client-side caching is the storage of network data to a local cache for future re-use.`}</p>
    <h2 {...{
      "id": "overview"
    }}>{`Overview`}</h2>
    <p>{`Client-server communication is slow and expensive.
Performance is an important feature for any Progressive Web Application (PWA), so
requests to the server should be minimized.`}</p>
    <p>{`Offline mode is also a required feature for a PWA.
In offline mode, the application must be able to serve pages that have been recently viewed.`}</p>
    <p>{`Applications implement these features with the help of a client-side cache.
After the application fetches network data, it stores that resource in a local cache.
Once a resource has been cached, the service worker uses the cache on future requests for that resource to boost performance.`}</p>
    <h2 {...{
      "id": "service-worker-caching"
    }}>{`Service Worker caching`}</h2>
    <p>{`A `}<a parentName="p" {...{
        "href": "https://developers.google.com/web/ilt/pwa/introduction-to-service-worker"
      }}>{`service worker`}</a>{` is a JavaScript file that runs in a separate thread from the main execution thread in a web application.
Service workers can intercept network requests and fetch cached data or store results from a network request into the cache.`}</p>
    <h3 {...{
      "id": "venia-service-worker"
    }}>{`Venia service worker`}</h3>
    <p>{`Venia's service worker uses Google's `}<a parentName="p" {...{
        "href": "https://developers.google.com/web/tools/workbox/"
      }}>{`Workbox`}</a>{` library.
Its behavior is defined in files inside the `}<a parentName="p" {...{
        "href": "https://github.com/magento/pwa-studio/blob/develop/packages/venia-concept/src/ServiceWorker/"
      }}>{`src/ServiceWorker/`}</a>{` directory.`}</p>
    <p>{`You do not need to use Workbox to define service worker behavior, but
Workbox makes this task easier by removing boilerplate code that is always used when working with service workers.`}</p>
    <p>{`Venia uses the following `}<a parentName="p" {...{
        "href": "https://developers.google.com/web/tools/workbox/modules/workbox-strategies"
      }}>{`caching strategies`}</a>{` with its service worker:`}</p>
    <h4 {...{
      "id": "stale-while-revalidate"
    }}>{`Stale-while-revalidate`}</h4>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://developers.google.com/web/fundamentals/instant-and-offline/offline-cookbook/#stale-while-revalidate"
      }}>{`stale-while-revalidate`}</a>{` strategy tells the service worker to use a cached response if it exists.
A separate network request is made for that resource and the cache is updated for future requests.`}</p>
    <p>{`This strategy is used when the most up to date version of a resource is not necessary for an application.`}</p>
    {/* prettier-ignore-start */}
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Route pattern`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`/`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The application root`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`/.`}{`\\`}{`.js$`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`JavaScript files`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`/\\/media\\/catalog.*`}{`.`}{`(?:png`}{`|`}{`gif`}{`|`}{`jpg`}{`|`}{`jpeg`}{`|`}{`svg)$/`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Catalog image files`}</td>
        </tr>
      </tbody>
    </table>
    {/* prettier-ignore-end */}
    <h4 {...{
      "id": "network-first"
    }}>{`Network first`}</h4>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://developers.google.com/web/fundamentals/instant-and-offline/offline-cookbook/#network-falling-back-to-cache"
      }}>{`network first`}</a>{` strategy tells the service worker to get a resource from the network first.
If a network connection cannot be made, the service worker uses the cache as a fallback.`}</p>
    <p>{`This strategy is used for data that may change frequently on the server.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Route pattern`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`\\`}{`.html$`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`HTML pages`}</td>
        </tr>
      </tbody>
    </table>
    <h4 {...{
      "id": "cache-first"
    }}>{`Cache first`}</h4>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://developers.google.com/web/fundamentals/instant-and-offline/offline-cookbook/#cache-falling-back-to-network"
      }}>{`cache first`}</a>{` strategy tells the service worker to use the data from the cache.
Unlike the stale-while-revalidate strategy, no network call is made to update the cache.`}</p>
    <p>{`If a response is not found in the cache, a network call is made to get the resource and cache the response.`}</p>
    <p>{`This strategy is used for non-critical assets that do not get updated very often.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Route pattern`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`images`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Image files served from the application`}</td>
        </tr>
      </tbody>
    </table>
    <p>{`When using service workers, you should add a width parameter to your images so that the correct image is returned from the cache.
If an image does not have a width parameter, a cached image with the same name but an incorrect size could be returned.`}</p>
    <h2 {...{
      "id": "caching-in-the-apollo-graphql-client"
    }}>{`Caching in the Apollo GraphQL client`}</h2>
    <p>{`The Venia implementation storefront uses the Apollo GraphQL client to make requests to the Adobe Commerce or Magento Open Source GraphQL endpoint.
It also incorporates the default `}<a parentName="p" {...{
        "href": "https://www.apollographql.com/docs/react/api/cache/InMemoryCache"
      }}>{`InMemoryCache`}</a>{` implementation to add caching abilities to the client.`}</p>
    <p>{`The cache is persisted between browser sessions in `}<inlineCode parentName="p">{`window.localstorage`}</inlineCode>{` using the `}<a parentName="p" {...{
        "href": "https://github.com/apollographql/apollo-cache-persist"
      }}>{`apollo-cache-persist`}</a>{` module.
This lets the Apollo client maintain its cached data even when the user closes the application.`}</p>
    <p>{`By default, InMemoryCache uses a cache first strategy for all queries.
This strategy is set using the `}<inlineCode parentName="p">{`fetchPolicy`}</inlineCode>{` prop on the `}<inlineCode parentName="p">{`Query`}</inlineCode>{` component.`}</p>
    <p>{`Caching for Apollo is set up in the `}<a parentName="p" {...{
        "href": "https://github.com/magento/pwa-studio/blob/master/packages/venia-ui/lib/drivers/adapter.js"
      }}>{`src/drivers/adapter.js`}</a>{` file.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      